import { Box, Stack } from '@mui/material'
import { ItemCardContentProps } from './ItemCard.types'
import {
  CardHeading,
  CardDesc,
  CardImage,
  CardChildren,
  LimitText,
  DescriptionStockContainer,
  StockText,
  StockValue,
  PriceText,
  PriceWrapper
} from './ItemCard.styles'
import { getMappedTagsProps, getPricePerPackageType } from './ItemCard.utils'
import Counter from '../Counter'
import Price from '../Price'
import Tags from '../Tags'
import PromotionDetail from '../PromotionDetail/PromotionDetail'
import PromotionLabel from '../PromotionLabel'
import {
  SkeletonDesc,
  SkeletonHeading,
  SkeletonPrice,
  SkeletonTags
} from './ItemCard.skeletons'
import useId from '../../hooks/useId'
import ProductOptions from '../ProductOptions'
import ProductOptionsList from '../ProductOptionsList'
import SelectButton from './components/SelectButton'
import PromotionsDetails from '../PromotionsDetails/PromotionsDetails'
import { ItemField } from '../../../interfaces/item'
import ProductPackages from '../ProductPackages'
import { IVolumePromotionDetail } from 'src/interfaces/catalog'
import Table from '../Table'

const ItemCardChildren = ({
  item = {
    quantity: 0
  },
  fields,
  styles,
  fieldsToDisplay,
  // components props
  name = {},
  description = {},
  limitText = {},
  stockText = {},
  stock = {},
  descriptionStock = {},
  image = {},
  price = {},
  priceTotal = {},
  counter = {},
  counterBundle = {},
  promotionDetail,
  promotionLabel,
  promotionTag,
  tags = {},
  mappedTags = [],
  horizontal = false,
  packagePriceMap = {},
  'data-testid': testid,
  bundlesHandler,
  bundleTexts,
  packageTexts,
  isOnCart,
  allowLazyLoadingOnImages = true,
  packagesProps,
  promotionsTable,
  priceText,
  ...props
}: ItemCardContentProps) => {
  const { getTestId } = useId(testid)
  const isEmpty = !Object.keys(item).length

  const getDescriptionComponent = (field: ItemField) => {
    const { value, ...descriptionProps } = description
    const descriptionValue = item?.description || value
    if (descriptionValue) {
      return (
        <CardDesc key={field} {...descriptionProps}>
          <span
            dangerouslySetInnerHTML={{ __html: descriptionValue }}
            data-testid={getTestId('description')}
          />
        </CardDesc>
      )
    }
    if (isEmpty) {
      return <SkeletonDesc key={field} {...descriptionProps} />
    }
  }

  const getStockComponent = () => {
    const { stock: stockValue } = item
    const { label, ...propsStockText } = stockText
    if (stockValue) {
      return (
        <Box>
          <StockText value={`${label || 'Stock'}: `} {...propsStockText} />
          <StockValue value={String(stockValue)} {...stock} />
        </Box>
      )
    }
    return null
  }
  return (
    <CardChildren sx={styles} {...props}>
      {fields?.map((field) => {
        // image
        if (field === ItemField.Image && fieldsToDisplay?.image !== false) {
          const { src, ...imageProps } = image
          const imageValue = item?.imageURL?.[0] || src || ''
          return (
            <CardImage
              key={field}
              src={imageValue}
              placeholderImage={src}
              data-testid={getTestId('image')}
              allowLazyLoading={allowLazyLoadingOnImages}
              {...imageProps}
            />
          )
        }
        // name
        if (field === ItemField.Name && fieldsToDisplay?.name !== false) {
          const { value, ...nameProps } = name
          const shouldDisplayPackageName =
            item.package && item.package.name && item.name
          const packageName = shouldDisplayPackageName
            ? `(${item?.package?.name}) ${item?.name}`
            : null
          const nameValue = packageName || item?.name || value
          if (nameValue) {
            return (
              <CardHeading
                key={field}
                horizontal={horizontal}
                data-testid={getTestId('name')}
                {...nameProps}>
                {nameValue}
              </CardHeading>
            )
          }
          if (isEmpty) {
            return <SkeletonHeading key={field} {...nameProps} />
          }
        }
        // description
        if (
          field === ItemField.Description &&
          fieldsToDisplay?.description !== false
        ) {
          return getDescriptionComponent(field)
        }
        // price
        if (
          field === ItemField.Price &&
          fieldsToDisplay?.price !== false &&
          !item?.hasDynamicPrice
        ) {
          const priceValue = getPricePerPackageType(
            {
              ...item,
              price: item?.price || price?.value
            },
            packagePriceMap
          )
          const unitsValue = item.unitsPerPackage || 1
          const { promotion, unitPrice } = item
          if (typeof priceValue === 'number') {
            return (
              <PriceWrapper>
                {priceText !== undefined ? (
                  <PriceText>{priceText}</PriceText>
                ) : null}
                <Price
                  key={field}
                  value={priceValue}
                  unitPrice={unitPrice}
                  units={unitsValue}
                  discount={promotion?.value}
                  showIcon={promotion?.value !== undefined}
                  data-testid={getTestId('price')}
                  align={props.align}
                  {...price}
                />
              </PriceWrapper>
            )
          }
          if (isEmpty) {
            return <SkeletonPrice key={field} size={price?.size} />
          }
        }

        // price total
        if (
          field === ItemField.PriceTotal &&
          fieldsToDisplay?.priceTotal !== false &&
          !item?.hasDynamicPrice
        ) {
          const { promotion, priceTotal: value } = item
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { value: _, ...priceTotalProps } = priceTotal

          if (typeof value === 'number') {
            return (
              <Price
                key={field}
                value={value}
                showIcon={promotion?.value !== undefined}
                data-testid={getTestId('priceTotal')}
                align={props.align}
                {...priceTotalProps}
              />
            )
          }
          if (isEmpty) {
            return <SkeletonPrice key={field} size={price?.size} />
          }
        }
        // tags
        if (field === ItemField.Tags && fieldsToDisplay?.tags !== false) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { map, items, ...tagsProps } = tags
          const tagsItems = getMappedTagsProps(mappedTags)
          if (tagsItems.length) {
            return (
              <Tags
                key={field}
                items={tagsItems}
                data-testid={getTestId('tags')}
                size="medium"
                align={props.align}
                {...tagsProps}
              />
            )
          }
          if (isEmpty) {
            return <SkeletonTags key={field} />
          }
        }
        // counter
        if (field === ItemField.Counter && fieldsToDisplay?.counter !== false) {
          const {
            quantity = 0,
            stock: stockValue,
            isHidden,
            readOnly,
            bundle,
            step: customStep
          } = item
          const {
            itemPropAsMin,
            min,
            disabled,
            allowSetMaxvalue,
            ...counterProps
          } = counter
          const isReadOnly = readOnly || isHidden
          const hasBundleItems =
            !!bundle?.items?.length || !!item?.subItems?.length
          const hasPackages = !!item?.extraPackages?.length
          const shouldShowSelectButtonBundles = hasBundleItems && !isOnCart
          const shouldShowSelectButtonPackages = hasPackages && !isOnCart
          const counterConfig =
            hasBundleItems && isOnCart
              ? { ...counterProps, ...counterBundle }
              : { ...counterProps }
          const defaultLabelForSelectButton = 'Select packages'
          const labelForBundleSelectButton =
            shouldShowSelectButtonBundles && bundleTexts?.selectButtonLabel
          const labelForPackageSelectButton =
            shouldShowSelectButtonPackages && packageTexts?.selectButtonLabel
          const labelForSelectButton =
            labelForBundleSelectButton ||
            labelForPackageSelectButton ||
            defaultLabelForSelectButton
          if (!isEmpty) {
            return shouldShowSelectButtonBundles ||
              shouldShowSelectButtonPackages ? (
              <SelectButton key={field} label={labelForSelectButton} />
            ) : (
              <Counter
                key={field}
                value={quantity}
                modifierName={item.packageType}
                {...counterConfig}
                disabled={disabled}
                readonly={isReadOnly}
                min={itemPropAsMin ? item[itemPropAsMin] : min}
                max={counter.max}
                stock={stockValue}
                data={item}
                data-testid={getTestId('counter')}
                allowSetMaxvalue={allowSetMaxvalue}
                customStep={customStep}
              />
            )
          }
        }
        // promotionDetail
        if (
          field === ItemField.PromotionDetail &&
          fieldsToDisplay?.promotionDetail !== false
        ) {
          const { promotion } = item
          if (promotion?.description) {
            return (
              <PromotionDetail
                showButton={promotion?.showButton}
                key={field}
                data={promotion.data}
                text={promotion?.description}
                secondaryText={promotion.secondaryText}
                image={promotion?.image}
                data-testid={getTestId('promotion-detail')}
                {...promotionDetail}
              />
            )
          }
        }
        // volumePromotions
        if (
          field === ItemField.VolumePromotions &&
          fieldsToDisplay?.volumePromotions !== false &&
          item.volumePromotionsResume &&
          !!item.volumePromotionsResume?.promotions.length &&
          !!promotionsTable?.columns
        ) {
          const {
            volumePromotionsResume: { promotions, selectedId }
          } = item
          return (
            promotions.length > 0 && (
              <Table<IVolumePromotionDetail>
                selectedId={selectedId}
                styles={promotionsTable?.styles}
                columns={promotionsTable.columns}
                data={promotions}
                data-testid={getTestId('volume-promotions')}
              />
            )
          )
        }
        // promotionDetails
        if (
          field === ItemField.PromotionsDetails &&
          fieldsToDisplay?.promotionsDetails !== false &&
          item.promotionsDetails
        ) {
          const {
            promotionsDetails: { title, secondaryTitle, details }
          } = item

          return (
            <Stack spacing={1} alignItems="flex-start">
              <PromotionDetail
                key={field}
                type="tag"
                text={title}
                secondaryText={secondaryTitle}
                data-testid={getTestId('promotion-tag')}
                showIcon
              />
              <PromotionsDetails
                details={details.map(
                  ({ buyText, discountText, savingText, id }) => ({
                    text: buyText,
                    secondaryText: discountText || '',
                    tertiaryText: savingText || '',
                    id
                  })
                )}
              />
            </Stack>
          )
        }
        // promotionTag
        if (
          field === ItemField.PromotionTag &&
          fieldsToDisplay?.promotionTag !== false
        ) {
          const { promotion = {} } = item
          const {
            label = '',
            limit,
            secondaryText = '',
            showIcon = false
          } = promotion

          const promotionTagProps = {
            lines: !limit && !secondaryText ? 2 : 1,
            ...promotionTag
          }
          if (label || limit) {
            return (
              <PromotionDetail
                showIcon={showIcon}
                key={field}
                type="tag"
                text={label}
                secondaryText={limit || secondaryText}
                data-testid={getTestId('promotion-tag')}
                {...promotionTagProps}
              />
            )
          }
        }
        // limitText
        if (
          field === ItemField.LimitText &&
          fieldsToDisplay?.limitText !== false
        ) {
          const { promotion = {} } = item
          const { limit, secondaryText = '' } = promotion
          if (secondaryText || limit) {
            return <LimitText value={secondaryText || limit} {...limitText} />
          }
        }

        // simplePromotionTag
        if (
          field === ItemField.SimplePromotionTag &&
          fieldsToDisplay?.simplePromotionTag !== false
        ) {
          const { promotion = {} } = item
          const { label = '', showIcon = true } = promotion
          const promotionTagProps = {
            lines: 2,
            styles: {
              padding: 0
            },
            ...promotionTag
          }
          if (label) {
            return (
              <PromotionDetail
                showIcon={showIcon}
                key={field}
                color="secondary"
                type="tag"
                text={label}
                data-testid={getTestId('promotion-tag')}
                {...promotionTagProps}
              />
            )
          }
        }

        // description
        if (field === ItemField.Stock && fieldsToDisplay?.stock !== false) {
          return getStockComponent()
        }
        // descriptionStock
        if (
          field === ItemField.DescriptionStock &&
          fieldsToDisplay?.descriptionStock !== false
        ) {
          return (
            <DescriptionStockContainer {...descriptionStock}>
              {getDescriptionComponent(field)}
              {getStockComponent()}
            </DescriptionStockContainer>
          )
        }
        // promotionOrigin
        if (
          field === ItemField.PromotionOrigin &&
          fieldsToDisplay?.promotionOrigin !== false
        ) {
          const { promotion } = item
          if (promotion?.promoOrigin) {
            return (
              <PromotionDetail
                key={field}
                text={promotion?.promoOrigin}
                data-testid={getTestId('promotion-origin')}
                {...promotionDetail}
              />
            )
          }
        }
        // promotion saving label
        if (
          field === ItemField.PromotionSavingLabel &&
          fieldsToDisplay?.promotionSavingLabel !== false
        ) {
          const { promotion } = item
          if (promotion?.saving) {
            return (
              <PromotionLabel
                key={field}
                text={promotion.saving}
                data-testid={getTestId('promotion-saving-label')}
                {...promotionLabel}
              />
            )
          }
        }
        if (
          field === ItemField.ProductOptions &&
          fieldsToDisplay?.productOptions !== false
        ) {
          const { bundle, bundlesExtra, subItem } = item
          const bundleItems = bundle && bundle.items && bundle.items.length
          if (
            bundleItems &&
            bundlesExtra &&
            bundlesHandler &&
            bundleTexts &&
            bundleTexts.label
          ) {
            return (
              <ProductOptions
                key={field}
                bundlesExtra={bundlesExtra}
                bundle={bundle}
                bundlesHandler={bundlesHandler}
                bundleTexts={bundleTexts}
                subItem={subItem}
              />
            )
          }
          return null
        }
        if (
          field === ItemField.ProductOptionsList &&
          fieldsToDisplay?.productOptionsList !== false
        ) {
          const { bundlesExtra = [], subItem } = item
          if (bundlesExtra.length && subItem) {
            return (
              <ProductOptionsList
                key={field}
                bundlesExtra={bundlesExtra}
                subItem={subItem}
              />
            )
          }
          return null
        }
        if (
          field === ItemField.ProductPackages &&
          fieldsToDisplay?.productPackages !== false
        ) {
          const { extraPackages } = item
          const { addToCart, removeFromCart, disabledActions } =
            packagesProps || {}
          const shouldDisplayProductPackages =
            extraPackages &&
            extraPackages.length > 0 &&
            addToCart &&
            removeFromCart &&
            packageTexts
          if (shouldDisplayProductPackages) {
            return (
              <ProductPackages
                product={item}
                key={field}
                packages={extraPackages}
                packageTexts={packageTexts}
                addToCart={addToCart}
                removeFromCart={removeFromCart}
                disabledActions={disabledActions}
              />
            )
          }
        }
        return null
      })}
    </CardChildren>
  )
}

export default ItemCardChildren
